import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { CellAlert } from '@liveline/ll_node_common/lib/models/websocket';
import { WebSocketState, LiveSignalData } from '../../types/websocket';

/**
 * A CellAlert as stored client-side: stamped with the browser ARRIVAL time.
 * Seen/unseen math must use received_ms, never the producer's time_stamp —
 * a plant-floor producer with a skewed clock would otherwise mint alerts
 * that arrive already "seen".
 */
export type StoredCellAlert = CellAlert & {
  received_ms: number;
  /**
   * How many times this same alert identity has folded into this row (1 = a
   * single occurrence, so it is left unset until the first repeat). A flapping
   * producer — a user alert whose value hovers at its threshold, or the
   * per-run-end "cleared on tracker cleanup" sweep — otherwise mints one row
   * per cycle and buries every other alert in the feed.
   */
  repeat_count?: number;
  /** time_stamp of the FIRST occurrence folded into this row. */
  first_time_stamp?: number;
};

export interface WebSocketSliceState {
  connections: {
    [facilityId: number]: WebSocketState;
  };
  liveSignalData: {
    [facilityId: number]: LiveSignalData;
  };
  cellAlerts: {
    [facilityId: number]: StoredCellAlert[];
  };
  globalError: string | null;
}

const initialState: WebSocketSliceState = {
  connections: {},
  liveSignalData: {},
  cellAlerts: {},
  globalError: null
};

const CELL_ALERTS_MAX = 200;

/**
 * Identity of the underlying CONDITION, independent of the individual
 * occurrence. `alert_config_id` is the producer's own identity for a
 * user-configured alert and is preferred when present; message is the
 * fallback, which keeps distinct user alerts sharing one code apart (a cell
 * emits "Tension" and "Ovality" both as USER_ALERT_TRIGGERED).
 */
function alertIdentity(alert: StoredCellAlert): string {
  return [
    alert.facility_line_id,
    alert.source,
    alert.code,
    alert.alert_config_id ?? alert.message,
  ].join('|');
}

/**
 * Fold incoming alerts into the stored list (newest first).
 * - same alert_id replaces the existing entry (mute-duration reminders re-emit the
 *   same id — update in place, never stack duplicates)
 * - state==='cleared' also retires any open 'raised' entry matching source+code+cell
 *   (producers may mint a fresh alert_id for the clear)
 * - a repeat CLEAR of the same identity folds into that row with a repeat_count
 *   instead of stacking a new one, so a flapping condition costs one line rather
 *   than one line per cycle
 */
export function foldCellAlerts(existing: StoredCellAlert[], incoming: StoredCellAlert[]): StoredCellAlert[] {
  let list = [...existing];
  incoming.forEach((alert) => {
    list = list.filter((a) => a.alert_id !== alert.alert_id);
    if (alert.state === 'cleared') {
      const matchIdx = list.findIndex(
        (a) =>
          a.state === 'raised' &&
          a.source === alert.source &&
          a.code === alert.code &&
          a.facility_line_id === alert.facility_line_id
      );
      if (matchIdx >= 0) {
        const open = list[matchIdx];
        list[matchIdx] = {
          ...open,
          state: 'cleared',
          time_stamp: alert.time_stamp,
          first_time_stamp: open.first_time_stamp ?? open.time_stamp,
        };
        return;
      }
    }
    // Repeat CLEAR of the same identity — fold in place, newest timestamp wins.
    // Scoped to 'cleared' on purpose:
    //   - 'raised' is an open condition: a distinct fact that keeps its own row
    //     (and its own severity styling).
    //   - 'event' is a point-in-time fact — RUN_STARTED / RUN_ENDED /
    //     PART_CHANGED. Consecutive runs of the same part produce an identical
    //     message ("Run started for part J1784"), so folding events would
    //     collapse four real runs into one line and destroy run history.
    if (alert.state === 'cleared') {
      const identity = alertIdentity(alert);
      const repeatIdx = list.findIndex(
        (a) => a.state === alert.state && alertIdentity(a) === identity
      );
      if (repeatIdx >= 0) {
        const prev = list[repeatIdx];
        const folded: StoredCellAlert = {
          ...prev,
          alert_id: alert.alert_id,
          time_stamp: alert.time_stamp,
          received_ms: alert.received_ms,
          message: alert.message,
          repeat_count: (prev.repeat_count ?? 1) + 1,
          first_time_stamp: prev.first_time_stamp ?? prev.time_stamp,
        };
        // Re-seat at the top: it is the most recent thing that happened.
        list.splice(repeatIdx, 1);
        list.unshift(folded);
        return;
      }
    }
    list.unshift(alert);
  });
  return list.slice(0, CELL_ALERTS_MAX);
}

const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    setConnectionState: (state, action: PayloadAction<{ facilityId: number; state: WebSocketState }>) => {
      const { facilityId, state: connectionState } = action.payload;
      if (connectionState.isConnected) {
        state.globalError = null;
      }
      state.connections[facilityId] = connectionState;
    },
    
    setLiveSignalData: (state, action: PayloadAction<{ facilityId: number; data: LiveSignalData }>) => {
      const { facilityId, data } = action.payload;
      state.liveSignalData[facilityId] = data;
    },
    
    clearLiveSignalData: (state, action: PayloadAction<number>) => {
      const facilityId = action.payload;
      delete state.liveSignalData[facilityId];
    },
    
    removeConnection: (state, action: PayloadAction<number>) => {
      const facilityId = action.payload;
      delete state.connections[facilityId];
      delete state.liveSignalData[facilityId];
    },
    
    addCellAlerts: (state, action: PayloadAction<{ facilityId: number; alerts: CellAlert[]; snapshot?: boolean }>) => {
      const { facilityId, alerts, snapshot } = action.payload;
      const receivedMs = Date.now();
      const stamped: StoredCellAlert[] = alerts.map((a) => ({ ...a, received_ms: receivedMs }));
      const existing = snapshot ? [] : (state.cellAlerts[facilityId] ?? []);
      state.cellAlerts[facilityId] = foldCellAlerts(existing, stamped);
    },

    clearCellAlerts: (state, action: PayloadAction<number>) => {
      delete state.cellAlerts[action.payload];
    },

    setGlobalError: (state, action: PayloadAction<string | null>) => {
      state.globalError = action.payload;
    },

    clearAllConnections: (state) => {
      state.connections = {};
      state.liveSignalData = {};
      state.cellAlerts = {};
      state.globalError = null;
    }
  }
});

export const {
  setConnectionState,
  setLiveSignalData,
  clearLiveSignalData,
  removeConnection,
  addCellAlerts,
  clearCellAlerts,
  setGlobalError,
  clearAllConnections
} = websocketSlice.actions;

export default websocketSlice.reducer; 